/**
 * Which guests a bulk action is about to touch.
 *
 * Tagging 200 people one at a time is why nobody would use tags at all, so the
 * bulk path is the feature and this is its arithmetic. Two things are decided
 * here rather than in the component, because both are easy to get subtly wrong
 * and neither is visible in a screenshot:
 *
 *  1. **The filter is the primary selector.** With nothing ticked, a bulk apply
 *     targets exactly the guests the filter left on screen — "coming, staying
 *     at the Bairro" is three taps and then one tag, where multi-selecting
 *     forty rows on a 375px phone is not a thing anyone finishes. Ticking rows
 *     overrides it; it is the fallback, not the mechanism.
 *
 *  2. **A selection is not narrowed by a filter changed afterwards.** A host
 *     ticks eight people and then types in the search box; five of them leave
 *     the screen. Silently dropping them would tag three people while the bar
 *     said eight, and silently keeping them invisible would tag five people the
 *     host cannot see. So the selection survives whole AND the count of hidden
 *     ones is stated — {@link selectionSummary} exists for exactly that
 *     sentence.
 */

export interface BulkTarget {
  inviteeIds: string[];
  /** Where the target came from, which decides the button's wording. */
  source: 'selection' | 'filter';
  /** The button label: "Tag these 12…" or "Tag 8 selected…". */
  label: string;
}

/**
 * Who a bulk apply would touch right now.
 *
 * The selected set wins whenever anything is ticked, whole — see the note above
 * about a filter changed after the fact.
 */
export function bulkTarget(
  visible: ReadonlyArray<{ inviteeId: string }>,
  selected: ReadonlySet<string>,
): BulkTarget {
  if (selected.size > 0) {
    return {
      inviteeIds: [...selected],
      source: 'selection',
      label: `Tag ${selected.size} selected…`,
    };
  }
  const inviteeIds = visible.map((guest) => guest.inviteeId);
  return {
    inviteeIds,
    source: 'filter',
    label: `Tag ${inviteeIds.length === 1 ? 'this guest' : `these ${inviteeIds.length}`}…`,
  };
}

/**
 * "8 selected · 5 not shown by this filter".
 *
 * The second clause only appears when it is true, and it is the whole reason
 * this function exists: a bulk apply that reaches guests who are off screen has
 * to say so before the host clicks, not after.
 */
export function selectionSummary(
  visible: ReadonlyArray<{ inviteeId: string }>,
  selected: ReadonlySet<string>,
): string {
  if (selected.size === 0) return '';
  const onScreen = new Set(visible.map((guest) => guest.inviteeId));
  const hidden = [...selected].filter((id) => !onScreen.has(id)).length;
  const head = `${selected.size} selected`;
  return hidden > 0 ? `${head} · ${hidden} not shown by this filter` : head;
}

/**
 * Ticking a row, with shift-click extending from the last one ticked.
 *
 * The anchor is an invitee id rather than an index so it survives the list
 * re-sorting or re-filtering underneath it; a range whose anchor has left the
 * screen degrades to a plain toggle rather than selecting an arbitrary block.
 */
export function toggleSelection(
  selected: ReadonlySet<string>,
  visible: ReadonlyArray<{ inviteeId: string }>,
  inviteeId: string,
  options: { extendFrom?: string | null } = {},
): Set<string> {
  const next = new Set(selected);
  const anchor = options.extendFrom ?? null;
  const from = anchor ? visible.findIndex((guest) => guest.inviteeId === anchor) : -1;
  const to = visible.findIndex((guest) => guest.inviteeId === inviteeId);

  if (anchor && from >= 0 && to >= 0) {
    // A range always ADDS. Shift-clicking to extend a selection and having it
    // clear the middle of the range is the classic surprise, and on a roster
    // the cost is tagging the wrong people.
    const [start, end] = from <= to ? [from, to] : [to, from];
    for (let index = start; index <= end; index += 1) next.add(visible[index].inviteeId);
    return next;
  }

  if (next.has(inviteeId)) next.delete(inviteeId);
  else next.add(inviteeId);
  return next;
}

/**
 * The header checkbox: ticks everything the filter left on screen, or clears
 * exactly those — never the ones it is hiding. A "select all" that also picks up
 * 125 guests scrolled out of existence by a search box is not select-all, it is
 * a mistake waiting for a bulk write.
 */
export function toggleAllVisible(
  selected: ReadonlySet<string>,
  visible: ReadonlyArray<{ inviteeId: string }>,
): Set<string> {
  const ids = visible.map((guest) => guest.inviteeId);
  const allOn = ids.length > 0 && ids.every((id) => selected.has(id));
  const next = new Set(selected);
  for (const id of ids) {
    if (allOn) next.delete(id);
    else next.add(id);
  }
  return next;
}

/** Header checkbox state: on, off, or partly. */
export function allVisibleState(
  selected: ReadonlySet<string>,
  visible: ReadonlyArray<{ inviteeId: string }>,
): 'none' | 'some' | 'all' {
  if (visible.length === 0) return 'none';
  const on = visible.filter((guest) => selected.has(guest.inviteeId)).length;
  if (on === 0) return 'none';
  return on === visible.length ? 'all' : 'some';
}

/**
 * Drops ids that are no longer on the roster.
 *
 * A guest deleted in another tab leaves a tick behind that no row can clear,
 * and the bulk apply would then send an id the API refuses — turning a whole
 * batch into an error over somebody who is not there any more.
 */
export function pruneSelection(
  selected: ReadonlySet<string>,
  roster: ReadonlyArray<{ inviteeId: string }>,
): Set<string> {
  const live = new Set(roster.map((guest) => guest.inviteeId));
  return new Set([...selected].filter((id) => live.has(id)));
}
